import apiClient from '../shared/apiClient';
import { startOfToday, parseISO } from 'date-fns';
import { 
  locationByClientId,
  orgUnitsByClientAndLevel,
  listHierarchyDefinitions,
  orgUnitEmployeesByOrgUnit,
  locationEmployeesByLocation
} from 'graphql/queries';

/**
 * Service for managing metrics and health data
 * Copied from locationAdminService.js with minimal changes
 */
class MetricsService {
  // Fetch overview metrics
  async getOverviewMetrics(clientId) {
    try {
      // Fetch locations using Amplify-generated query

      const locationsResult = await apiClient.query(locationByClientId, { clientId });

      const locations = locationsResult?.data?.locationByClientId?.items || [];

      // Fetch org units using Amplify-generated query

      const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });

      const orgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];

      // Fetch manager assignments to properly count units with managers
      const managerCheckPromises = orgUnits.map(async (unit) => {
        try {
          const assignmentResult = await apiClient.query(orgUnitEmployeesByOrgUnit, {
            orgUnitId: unit.id,
            filter: { 
              isActive: { eq: true },
              clientId: { eq: clientId }
            },
            limit: 1
          });
          return assignmentResult?.data?.orgUnitEmployeesByOrgUnit?.items?.length > 0;
        } catch (error) {
          console.error(`Error checking manager for unit ${unit.id}:`, error);
          return false;
        }
      });
      
      const managerCheckResults = await Promise.all(managerCheckPromises);
      const unitsWithManagersCount = managerCheckResults.filter(hasManager => hasManager).length;

      // Check for location managers as well
      const locationManagerCheckPromises = locations.map(async (location) => {
        try {
          const locationId = location.id || location.locationId || location.ID;
          if (!locationId) return false;
          
          const assignmentResult = await apiClient.query(locationEmployeesByLocation, {
            locationId: locationId,
            filter: { 
              isActive: { eq: true },
              clientId: { eq: clientId }
            },
            limit: 1
          });
          return assignmentResult?.data?.locationEmployeesByLocation?.items?.length > 0;
        } catch (error) {
          console.error(`Error checking manager for location ${location.locationName}:`, error);
          return false;
        }
      });
      
      const locationManagerCheckResults = await Promise.all(locationManagerCheckPromises);
      const locationsWithManagersCount = locationManagerCheckResults.filter(hasManager => hasManager).length;

      // Calculate metrics based on closeDate
      const totalLocations = locations.length;
      const today = startOfToday();
      
      // Categorize locations based on closeDate
      let activeLocations = 0;
      let closedLocations = 0;
      let closingLocations = 0;
      let unknownStatusLocations = 0;
      
      locations.forEach(loc => {
        if (!loc.closeDate || loc.closeDate === null) {
          activeLocations++;
        } else if (typeof loc.closeDate === 'string') {
          try {
            const closeDateObj = parseISO(loc.closeDate);
            if (closeDateObj <= today) {
              closedLocations++;
            } else {
              closingLocations++; // Future close date
            }
          } catch (e) {
            unknownStatusLocations++;
          }
        } else {
          unknownStatusLocations++;
        }
      });
      
      // For backward compatibility, combine closed and closing as "inactive"
      const inactiveLocations = closedLocations + closingLocations;

      const unassignedLocations = locations.filter(l => !l.orgUnitId && !l.orgLevel_1 && !l.orgLevel_2 && !l.orgLevel_3).length;
      const unitsWithoutManagers = orgUnits.length - unitsWithManagersCount;
      const locationsWithoutManagers = totalLocations - locationsWithManagersCount;
      const assignedLocations = totalLocations - unassignedLocations;
      const unitsWithManagers = unitsWithManagersCount;
      const locationsWithManagers = locationsWithManagersCount;
      const validStatusLocations = totalLocations - unknownStatusLocations;

      // Get hierarchy definition to understand org levels
      const hierarchyDefResult = await apiClient.query(listHierarchyDefinitions, { 
        filter: { 
          clientId: { eq: clientId },
          endDate: { attributeExists: false }
        },
        limit: 1
      });
      
      const hierarchyDef = hierarchyDefResult?.data?.listHierarchyDefinitions?.items?.[0];
      let orgUnitBreakdown = [];
      
      if (hierarchyDef && hierarchyDef.levels) {
        // Count org units by level and prepare breakdown
        const unitsByLevel = {};
        orgUnits.forEach(unit => {
          if (!unitsByLevel[unit.orgLevel]) {
            unitsByLevel[unit.orgLevel] = { count: 0, levelId: unit.orgLevel };
          }
          unitsByLevel[unit.orgLevel].count++;
        });
        
        // Get the level definitions and sort by order (higher levels first)
        const activeLevels = hierarchyDef.levels
          .filter(level => level.isUsed && level.levelId.startsWith('ORGLEVEL'))
          .sort((a, b) => b.order - a.order);
        
        // Create breakdown with proper labels
        orgUnitBreakdown = activeLevels.map(level => {
          const count = unitsByLevel[level.levelId]?.count || 0;
          const label = level.label.toLowerCase();
          
          // Pluralize the label
          let pluralLabel = label;
          if (count !== 1) {
            if (label.endsWith('y') && !['ay', 'ey', 'oy', 'uy'].some(v => label.endsWith(v))) {
              pluralLabel = `${label.slice(0, -1)}ies`;
            } else if (label.endsWith('s') || label.endsWith('x') || label.endsWith('ch')) {
              pluralLabel = label.endsWith('s') ? label : `${label}es`;
            } else {
              pluralLabel = `${label}s`;
            }
          }
          
          return {
            count,
            label: pluralLabel,
            levelId: level.levelId,
            order: level.order
          };
        }).filter(item => item.count > 0); // Only show levels that have units
      }

      // Get actual system health data
      let systemHealthData = null;
      let totalIssues = 0;
      let healthScore = 0;
      
      try {
        systemHealthData = await this.performHealthChecks(clientId);
        totalIssues = systemHealthData.issues.length;
        healthScore = systemHealthData.overallCompletion;
      } catch (error) {
        console.error('Error getting system health for metrics:', error);
        // Fallback to calculated values
        totalIssues = unassignedLocations + unknownStatusLocations + unitsWithoutManagers + locationsWithoutManagers;
        
        // Calculate health score if system health check failed
        if (totalLocations > 0 || orgUnits.length > 0) {
          const assignmentScore = totalLocations > 0 ? (assignedLocations / totalLocations) : 1;
          const statusScore = totalLocations > 0 ? (validStatusLocations / totalLocations) : 1;
          const unitManagerScore = orgUnits.length > 0 ? (unitsWithManagers / orgUnits.length) : 1;
          const locationManagerScore = totalLocations > 0 ? (locationsWithManagers / totalLocations) : 1;
          // Weight: 25% assignment, 25% status, 25% unit managers, 25% location managers
          healthScore = Math.round((assignmentScore * 0.25 + statusScore * 0.25 + unitManagerScore * 0.25 + locationManagerScore * 0.25) * 100);
        }
      }

      const result = {
        totalLocations,
        activeLocations,
        inactiveLocations,
        unknownStatusLocations,
        orgUnits: orgUnits.length,
        orgUnitBreakdown,
        unassignedLocations,
        unitsWithoutManagers,
        locationsWithoutManagers,
        unitsWithManagers,
        locationsWithManagers,
        hierarchyHealth: healthScore,
        issues: totalIssues
      };

      return result;
    } catch (error) {
      console.error('❌ Error fetching overview metrics:', error);
      console.error('Error details:', {
        message: error.message,
        errors: error.errors,
        data: error.data
      });
      throw error;
    }
  }

  // Get system health issues
  async getSystemHealthIssues(clientId) {
    try {
      const metrics = await this.getOverviewMetrics(clientId);
      
      const issues = [];
      
      if (metrics.unknownStatusLocations > 0) {
        issues.push({
          type: 'unknown_status',
          severity: 'error',
          title: 'Invalid Location Status',
          description: `${metrics.unknownStatusLocations} locations have invalid closeDate values`,
          action: 'Fix Now',
          icon: 'error'
        });
      }
      
      if (metrics.unassignedLocations > 0) {
        issues.push({
          type: 'unassigned_locations',
          severity: 'warning',
          title: 'Unassigned Locations',
          description: `${metrics.unassignedLocations} locations need hierarchy assignment`,
          action: 'Fix Now',
          icon: 'warning'
        });
      }

      if (metrics.unitsWithoutManagers > 0) {
        issues.push({
          type: 'unassigned_managers',
          severity: 'warning',
          title: 'Unassigned Managers',
          description: `${metrics.unitsWithoutManagers} units need manager assignment`,
          action: 'Assign',
          icon: 'person_off'
        });
      }

      return {
        issues,
        overallCompletion: metrics.hierarchyHealth
      };
    } catch (error) {
      console.error('Error fetching system health:', error);
      throw error;
    }
  }

  // Get system health
  async getSystemHealth(clientId) {
    try {
      return await this.performHealthChecks(clientId);
    } catch (error) {
      console.error('❌ Error getting system health:', error);
      throw error;
    }
  }

  /**
   * Performs comprehensive system health checks for a client's location hierarchy
   * @param {string} clientId - The client ID to check
   * @returns {Object} Health check results with issues and metrics
   */
  async performHealthChecks(clientId) {
    const issues = [];
    const passedChecks = [];
    const metrics = {
      totalChecks: 0,
      passedChecks: 0,
      warnings: 0,
      errors: 0
    };

    try {
      // 1. Fetch all required data
      const [locationsResult, orgUnitsResult, hierarchyDefResult] = await Promise.all([
        apiClient.query(locationByClientId, { clientId }),
        apiClient.query(orgUnitsByClientAndLevel, { clientId }),
        apiClient.query(listHierarchyDefinitions, { 
          filter: { 
            clientId: { eq: clientId },
            endDate: { attributeExists: false }
          },
          limit: 1
        })
      ]);

      const locations = locationsResult?.data?.locationByClientId?.items || [];
      const orgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
      const hierarchyDef = hierarchyDefResult?.data?.listHierarchyDefinitions?.items?.[0];

      // Determine active hierarchy levels
      const activeLevels = hierarchyDef?.levels?.filter(l => l.isUsed) || [];
      const hasOrgLevel1 = activeLevels.some(l => l.levelId === 'ORGLEVEL_1');
      const hasOrgLevel2 = activeLevels.some(l => l.levelId === 'ORGLEVEL_2');
      const hasOrgLevel3 = activeLevels.some(l => l.levelId === 'ORGLEVEL_3');

      // 2. Check: All locations assigned to OrgUnit (if OrgLevel_1 exists)
      if (hasOrgLevel1) {
        metrics.totalChecks++;
        const unassignedLocations = locations.filter(loc => 
          !loc.orgUnitId && !loc.orgLevel_1
        );
        
        if (unassignedLocations.length === 0) {
          metrics.passedChecks++;
          passedChecks.push({
            id: 'locations-assigned',
            title: 'All locations assigned to organizational units',
            category: 'location-assignment'
          });
        } else {
          metrics.errors++;
          issues.push({
            id: 'unassigned-locations',
            severity: 'error',
            category: 'location-assignment',
            title: `${unassignedLocations.length} Unassigned Location${unassignedLocations.length !== 1 ? 's' : ''}`,
            description: `Location${unassignedLocations.length !== 1 ? 's' : ''} not assigned to any organizational unit`,
            action: 'Assign',
            icon: 'store',
            details: unassignedLocations.slice(0, 5).map(l => ({
              id: l.id,
              name: l.locationName,
              locationNumber: l.locationNumber
            })),
            totalCount: unassignedLocations.length
          });
        }
      }

      // 3. Check: Parent OrgUnits assigned (for multi-level hierarchies)
      if (hasOrgLevel2 || hasOrgLevel3) {
        metrics.totalChecks++;
        const orphanedUnits = [];
        
        orgUnits.forEach(unit => {
          // Check ORGLEVEL_1 units need parent if ORGLEVEL_2 exists
          if (unit.orgLevel === 'ORGLEVEL_1' && hasOrgLevel2 && !unit.parentOrgUnitId) {
            orphanedUnits.push(unit);
          }
          // Check ORGLEVEL_2 units need parent if ORGLEVEL_3 exists
          if (unit.orgLevel === 'ORGLEVEL_2' && hasOrgLevel3 && !unit.parentOrgUnitId) {
            orphanedUnits.push(unit);
          }
        });
        
        if (orphanedUnits.length === 0) {
          metrics.passedChecks++;
          passedChecks.push({
            id: 'hierarchy-structure',
            title: 'All organizational units properly connected in hierarchy',
            category: 'hierarchy-structure'
          });
        } else {
          metrics.warnings++;
          issues.push({
            id: 'orphaned-units',
            severity: 'warning',
            category: 'hierarchy-structure',
            title: `${orphanedUnits.length} Orphaned Unit${orphanedUnits.length !== 1 ? 's' : ''}`,
            description: `Organizational unit${orphanedUnits.length !== 1 ? 's' : ''} without parent assignment`,
            action: 'Fix Hierarchy',
            icon: 'account_tree',
            details: orphanedUnits.slice(0, 5).map(u => ({
              id: u.id,
              name: u.name,
              level: u.orgLevel
            })),
            totalCount: orphanedUnits.length
          });
        }
      }

      // 4. Check: All OrgUnits have managers - broken down by org level
      const unitsWithoutManagers = [];
      const unitsByLevelWithoutManagers = {};
      
      // Check manager assignments for each org unit using OrgUnitEmployee
      const managerCheckPromises = orgUnits.map(async (unit) => {
        try {
          const assignmentResult = await apiClient.query(orgUnitEmployeesByOrgUnit, {
            orgUnitId: unit.id,
            filter: { 
              isActive: { eq: true },
              clientId: { eq: clientId }
            },
            limit: 1
          });
          const hasManager = assignmentResult?.data?.orgUnitEmployeesByOrgUnit?.items?.length > 0;
          if (!hasManager) {
            unitsWithoutManagers.push(unit);
            if (!unitsByLevelWithoutManagers[unit.orgLevel]) {
              unitsByLevelWithoutManagers[unit.orgLevel] = [];
            }
            unitsByLevelWithoutManagers[unit.orgLevel].push(unit);
          }
          return { unitId: unit.id, hasManager };
        } catch (error) {
          console.error(`Error checking manager for unit ${unit.id}:`, error);
          unitsWithoutManagers.push(unit);
          if (!unitsByLevelWithoutManagers[unit.orgLevel]) {
            unitsByLevelWithoutManagers[unit.orgLevel] = [];
          }
          unitsByLevelWithoutManagers[unit.orgLevel].push(unit);
          return { unitId: unit.id, hasManager: false };
        }
      });
      
      await Promise.all(managerCheckPromises);
      
      // Create separate issues for each org level that has units without managers
      if (unitsWithoutManagers.length === 0) {
        metrics.totalChecks++;
        metrics.passedChecks++;
        passedChecks.push({
          id: 'units-have-managers',
          title: 'All organizational units have managers assigned',
          category: 'manager-assignment'
        });
      } else {
        // Create an issue for each org level with units missing managers
        Object.keys(unitsByLevelWithoutManagers).forEach(levelId => {
          metrics.totalChecks++;
          const unitsAtLevel = unitsByLevelWithoutManagers[levelId];
          
          // Find the level label from hierarchy definition
          const levelDef = activeLevels.find(l => l.levelId === levelId);
          const levelLabel = levelDef ? levelDef.label : levelId;
          
          // Pluralize the label for multiple units
          let pluralLabel = levelLabel;
          if (unitsAtLevel.length !== 1) {
            const labelLower = levelLabel.toLowerCase();
            if (labelLower.endsWith('y') && !['ay', 'ey', 'oy', 'uy'].some(v => labelLower.endsWith(v))) {
              pluralLabel = `${levelLabel.slice(0, -1)}ies`;
            } else if (labelLower.endsWith('s') || labelLower.endsWith('x') || labelLower.endsWith('ch')) {
              pluralLabel = labelLower.endsWith('s') ? levelLabel : `${levelLabel}es`;
            } else {
              pluralLabel = `${levelLabel}s`;
            }
          }
          
          metrics.warnings++;
          issues.push({
            id: `units-without-managers-${levelId}`,
            severity: 'warning',
            category: 'manager-assignment',
            title: `${unitsAtLevel.length} ${pluralLabel} Without Manager`,
            description: `${pluralLabel} ${unitsAtLevel.length !== 1 ? 'need' : 'needs'} manager assignment`,
            action: 'Assign Manager',
            icon: 'person_off',
            details: unitsAtLevel.slice(0, 5).map(u => ({
              id: u.id,
              name: u.name
            })),
            totalCount: unitsAtLevel.length
          });
        });
      }

      // 5. Check: All active Locations have managers
      metrics.totalChecks++;
      // Check for location managers using LocationEmployee
      const locationManagerCheckPromises = locations.map(async (location) => {
        // First determine if location is active based on closeDate
        let isActive = true;
        if (location.closeDate) {
          try {
            const closeDate = parseISO(location.closeDate);
            const today = startOfToday();
            if (closeDate <= today) {
              isActive = false; // Location is closed
            }
          } catch (e) {
            // Invalid date, consider it active but it will be caught by date validity check
            isActive = true;
          }
        }
        
        if (!isActive) {
          return { location, hasManager: true }; // Don't check closed locations
        }
        
        try {
          const locationId = location.id || location.locationId || location.ID;
          if (!locationId) return { location, hasManager: false };
          
          const assignmentResult = await apiClient.query(locationEmployeesByLocation, {
            locationId: locationId,
            filter: { 
              isActive: { eq: true },
              clientId: { eq: clientId }
            },
            limit: 1
          });
          const hasManager = assignmentResult?.data?.locationEmployeesByLocation?.items?.length > 0;
          return { location, hasManager };
        } catch (error) {
          console.error(`Error checking manager for location ${location.locationName}:`, error);
          return { location, hasManager: false };
        }
      });
      
      const locationManagerResults = await Promise.all(locationManagerCheckPromises);
      const locationsWithoutManagers = locationManagerResults
        .filter(result => !result.hasManager)
        .map(result => result.location);
      
      if (locationsWithoutManagers.length === 0) {
        metrics.passedChecks++;
        passedChecks.push({
          id: 'locations-have-managers',
          title: 'All active locations have managers assigned',
          category: 'manager-assignment'
        });
      } else {
        metrics.warnings++;
        issues.push({
          id: 'locations-without-managers',
          severity: 'warning',
          category: 'manager-assignment',
          title: `${locationsWithoutManagers.length} Location${locationsWithoutManagers.length !== 1 ? 's' : ''} Without Manager`,
          description: `Location${locationsWithoutManagers.length !== 1 ? 's need' : ' needs'} manager assignment`,
          action: 'Assign Manager',
          icon: 'person_off',
          details: locationsWithoutManagers.slice(0, 5).map(l => ({
            id: l.id,
            name: l.locationName,
            locationNumber: l.locationNumber
          })),
          totalCount: locationsWithoutManagers.length
        });
      }

      // 6. Check: Location status validity
      metrics.totalChecks++;
      const today = startOfToday();
      
      const invalidStatusLocations = [];
      locations.forEach(loc => {
        // In this system, closeDate is the primary indicator of status:
        // - No closeDate or null = Active
        // - closeDate in the past = Should be closed
        // - closeDate in the future = Closing soon
        
        // Check for invalid date formats
        if (loc.closeDate && typeof loc.closeDate === 'string') {
          try {
            const closeDate = parseISO(loc.closeDate);
            // Valid date - no issue here, this is expected behavior
          } catch (e) {
            invalidStatusLocations.push({
              ...loc,
              issue: 'invalid-date'
            });
          }
        } else if (loc.closeDate && typeof loc.closeDate !== 'string') {
          // closeDate exists but is not a string - this is invalid
          invalidStatusLocations.push({
            ...loc,
            issue: 'invalid-date-type'
          });
        }
        
        // Note: We're NOT checking isActive field as closeDate is the source of truth
        // The isActive field may not exist or may be inconsistent with closeDate
      });
      
      if (invalidStatusLocations.length === 0) {
        metrics.passedChecks++;
        passedChecks.push({
          id: 'valid-close-dates',
          title: 'All location close dates are valid',
          category: 'data-integrity'
        });
      } else {
        metrics.errors++;
        issues.push({
          id: 'invalid-location-dates',
          severity: 'error',
          category: 'data-integrity',
          title: `${invalidStatusLocations.length} Invalid Close Date${invalidStatusLocations.length !== 1 ? 's' : ''}`,
          description: `Location${invalidStatusLocations.length !== 1 ? 's have' : ' has'} invalid or malformed close date values`,
          action: 'Fix Dates',
          icon: 'date_range',
          details: invalidStatusLocations.slice(0, 5).map(l => ({
            id: l.id,
            name: l.locationName,
            issue: l.issue === 'invalid-date' ? 'Invalid date format' : 
                   l.issue === 'invalid-date-type' ? 'Close date is not a string' : 
                   'Unknown date issue'
          })),
          totalCount: invalidStatusLocations.length
        });
      }

      // 7. Check: Duplicate location numbers
      metrics.totalChecks++;
      const locationNumbers = {};
      const duplicateLocations = [];
      
      locations.forEach(loc => {
        if (loc.locationNumber) {
          if (locationNumbers[loc.locationNumber]) {
            if (!duplicateLocations.some(d => d.locationNumber === loc.locationNumber)) {
              duplicateLocations.push({
                locationNumber: loc.locationNumber,
                locations: [locationNumbers[loc.locationNumber], loc]
              });
            } else {
              const dup = duplicateLocations.find(d => d.locationNumber === loc.locationNumber);
              dup.locations.push(loc);
            }
          } else {
            locationNumbers[loc.locationNumber] = loc;
          }
        }
      });
      
      if (duplicateLocations.length === 0) {
        metrics.passedChecks++;
        passedChecks.push({
          id: 'unique-location-numbers',
          title: 'All location numbers are unique',
          category: 'data-integrity'
        });
      } else {
        metrics.errors++;
        issues.push({
          id: 'duplicate-location-numbers',
          severity: 'error',
          category: 'data-integrity',
          title: `${duplicateLocations.length} Duplicate Number${duplicateLocations.length !== 1 ? 's' : ''}`,
          description: `Location number${duplicateLocations.length !== 1 ? 's are' : ' is'} used multiple times`,
          action: 'Resolve',
          icon: 'content_copy',
          details: duplicateLocations.slice(0, 3).map(d => ({
            locationNumber: d.locationNumber,
            count: d.locations.length,
            names: d.locations.map(l => l.locationName).join(', ')
          })),
          totalCount: duplicateLocations.length
        });
      }

      // 8. Check: Circular hierarchy references
      metrics.totalChecks++;
      const circularReferences = this.detectCircularReferences(orgUnits);
      
      if (circularReferences.length === 0) {
        metrics.passedChecks++;
        passedChecks.push({
          id: 'no-circular-references',
          title: 'No circular references in hierarchy',
          category: 'hierarchy-structure'
        });
      } else {
        metrics.errors++;
        issues.push({
          id: 'circular-references',
          severity: 'error',
          category: 'hierarchy-structure',
          title: 'Circular References Detected',
          description: 'Organizational units have circular parent-child relationships',
          action: 'Fix Structure',
          icon: 'loop',
          details: circularReferences.slice(0, 3),
          totalCount: circularReferences.length
        });
      }

      // 9. Check: Inactive units with active children
      metrics.totalChecks++;
      const inconsistentInactiveUnits = [];
      
      orgUnits.forEach(unit => {
        if (unit.isActive === false) {
          // Check if this unit has active children
          const activeChildren = orgUnits.filter(u => 
            u.parentOrgUnitId === unit.id && u.isActive !== false
          );
          
          if (activeChildren.length > 0) {
            inconsistentInactiveUnits.push({
              parent: unit,
              activeChildrenCount: activeChildren.length
            });
          }
        }
      });
      
      if (inconsistentInactiveUnits.length === 0) {
        metrics.passedChecks++;
        passedChecks.push({
          id: 'consistent-status',
          title: 'Parent/child status consistency maintained',
          category: 'hierarchy-structure'
        });
      } else {
        metrics.warnings++;
        issues.push({
          id: 'inactive-with-active-children',
          severity: 'warning',
          category: 'hierarchy-structure',
          title: `${inconsistentInactiveUnits.length} Inconsistent Status${inconsistentInactiveUnits.length !== 1 ? 'es' : ''}`,
          description: `Inactive unit${inconsistentInactiveUnits.length !== 1 ? 's have' : ' has'} active children`,
          action: 'Review',
          icon: 'warning',
          details: inconsistentInactiveUnits.slice(0, 3).map(i => ({
            name: i.parent.name,
            activeChildren: i.activeChildrenCount
          })),
          totalCount: inconsistentInactiveUnits.length
        });
      }

      // Calculate overall health score
      const overallCompletion = Math.round((metrics.passedChecks / metrics.totalChecks) * 100);

      // Sort issues by severity (errors first, then warnings)
      issues.sort((a, b) => {
        const severityOrder = { error: 0, warning: 1, info: 2 };
        return severityOrder[a.severity] - severityOrder[b.severity];
      });

      return {
        issues,
        passedChecks,
        metrics,
        overallCompletion,
        summary: {
          totalLocations: locations.length,
          totalOrgUnits: orgUnits.length,
          hierarchyLevels: activeLevels.length,
          criticalIssues: metrics.errors,
          warnings: metrics.warnings
        }
      };

    } catch (error) {
      console.error('❌ Error performing health checks:', error);
      throw error;
    }
  }

  /**
   * Detects circular references in the organizational hierarchy
   */
  detectCircularReferences(orgUnits) {
    const circularRefs = [];
    const unitMap = new Map(orgUnits.map(u => [u.id, u]));
    
    orgUnits.forEach(unit => {
      const visited = new Set();
      let current = unit;
      
      while (current && current.parentOrgUnitId) {
        if (visited.has(current.id)) {
          // Circular reference detected
          circularRefs.push({
            unitId: unit.id,
            unitName: unit.name,
            cycle: Array.from(visited).map(id => unitMap.get(id)?.name || id).join(' → ')
          });
          break;
        }
        
        visited.add(current.id);
        current = unitMap.get(current.parentOrgUnitId);
        
        // Safety check to prevent infinite loops
        if (visited.size > 100) {
          console.error('Possible infinite loop detected in hierarchy');
          break;
        }
      }
    });
    
    return circularRefs;
  }
}

export default new MetricsService();